import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Leaf, Clock, Shield, Star, ChevronRight, Euro, Calendar, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import DiagonalScroll from '@/components/DiagonalScroll';
import ReviewCard from '@/components/ReviewCard';
import BlogCard from '@/components/BlogCard';
import { useSiteContentBatch } from '@/hooks/useSiteContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_FALLBACK_IMAGE = "https://images.pexels.com/photos/5082949/pexels-photo-5082949.jpeg?auto=compress&cs=tinysrgb&w=1920";

// Function to open reservation modal
const openReservationModal = () => {
  window.dispatchEvent(new CustomEvent('openReservationModal'));
};

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch editable content from CMS
  const content = useSiteContentBatch([
    { key: 'homepage_hero_badge', defaultValue: 'Première solution d\'abonnement et de réservation en ligne en France pour l\'entretien de jardin' },
    { key: 'homepage_hero_title_1', defaultValue: 'avoir un' },
    { key: 'homepage_hero_title_highlight', defaultValue: 'BEAU JARDIN' },
    { key: 'homepage_hero_title_2', defaultValue: 'c\'est' },
    { key: 'homepage_hero_title_accent', defaultValue: 'EASY' },
    { key: 'homepage_hero_description', defaultValue: 'Confiez l\'entretien de votre jardin à des professionnels passionnés. Abonnement mensuel ou intervention ponctuelle.' },
    { key: 'homepage_cta_primary', defaultValue: 'Réserver mon entretien de jardin' },
    { key: 'homepage_cta_secondary', defaultValue: 'Intervention ponctuelle' },
    { key: 'homepage_stat_1_value', defaultValue: '10+' },
    { key: 'homepage_stat_1_label', defaultValue: 'Années d\'expérience' },
    { key: 'homepage_stat_2_value', defaultValue: '500+' },
    { key: 'homepage_stat_2_label', defaultValue: 'Jardins entretenus' },
    { key: 'homepage_stat_3_value', defaultValue: '98%' },
    { key: 'homepage_stat_3_label', defaultValue: 'Clients satisfaits' },
    { key: 'homepage_stat_4_value', defaultValue: '24h' },
    { key: 'homepage_stat_4_label', defaultValue: 'Délai intervention' },
    { key: 'homepage_features_title', defaultValue: 'L\'excellence au service de votre jardin' },
    { key: 'homepage_services_title', defaultValue: 'Des services adaptés à tous vos besoins' },
    { key: 'homepage_reviews_title', defaultValue: 'Ce que nos clients disent' },
    { key: 'homepage_reviews_rating', defaultValue: '4.9/5 basé sur 127 avis' },
    { key: 'homepage_cta_title', defaultValue: 'Prêt à transformer votre jardin ?' },
    { key: 'homepage_cta_description', defaultValue: 'Contactez-nous dès aujourd\'hui pour un devis gratuit et personnalisé. Notre équipe vous accompagne dans tous vos projets d\'entretien.' },
    { key: 'homepage_blog_title', defaultValue: 'Nos derniers articles' },
    { key: 'global_youtube_video_id', defaultValue: 'g_NuDrtC9RM' },
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`).catch(() => {});
        
        const [servicesRes, reviewsRes, blogRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/reviews`),
          axios.get(`${API}/blog?limit=3`)
        ]);
        
        setServices(servicesRes.data);
        setReviews(reviewsRes.data);
        setBlogPosts(blogRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const stats = [
    { value: content.homepage_stat_1_value, label: content.homepage_stat_1_label },
    { value: content.homepage_stat_2_value, label: content.homepage_stat_2_label },
    { value: content.homepage_stat_3_value, label: content.homepage_stat_3_label },
    { value: content.homepage_stat_4_value, label: content.homepage_stat_4_label },
  ];

  const features = [
    { icon: Leaf, title: 'Expertise locale', description: 'Connaissance parfaite des plantes méditerranéennes' },
    { icon: Clock, title: 'Flexibilité', description: 'Abonnement ou intervention ponctuelle selon vos besoins' },
    { icon: Shield, title: 'Qualité garantie', description: 'Satisfaction assurée ou reprise gratuite' },
  ];

  const YOUTUBE_VIDEO_ID = content.global_youtube_video_id;

  return (
    <Layout transparentHeader={true}>
      {/* Hero Section with YouTube Video Background */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* YouTube Video Background - Works on all devices including mobile */}
        <div className="absolute inset-0 z-0 video-background-container">
          <iframe
            src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${YOUTUBE_VIDEO_ID}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${window.location.origin}&vq=hd720`}
            title="Background video"
            className="pointer-events-none"
            style={{ border: 'none' }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="eager"
          />
          {/* Fallback gradient background in case video doesn't load */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 -z-10"></div>
        </div>
        
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black/85 via-black/70 to-black/50"></div>

        {/* Animated light effects */}
        <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-40 left-20 w-80 h-80 bg-yellow-400/15 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-teal-400/10 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container-custom py-20 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white text-sm font-medium px-5 py-2.5 rounded-full mb-8 animate-fade-in border border-white/20">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
              {content.homepage_hero_badge}
            </div>
            
            <h1 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.15] mb-4 animate-slide-up">
              {content.homepage_hero_title_1}{' '}
              <span className="text-gradient-hero">{content.homepage_hero_title_highlight}</span>
            </h1>
            
            <div className="flex items-center justify-center gap-4 mb-8 animate-slide-up" style={{ animationDelay: '0.05s' }}>
              <div className="h-[2px] w-12 md:w-20 bg-gradient-to-r from-transparent to-emerald-400"></div>
              <p className="font-heading text-3xl sm:text-4xl md:text-5xl text-white">
                {content.homepage_hero_title_2}{' '}
                <span className="font-accent text-emerald-400 text-4xl sm:text-5xl md:text-6xl lg:text-7xl relative inline-block">
                  {content.homepage_hero_title_accent}
                  <span className="absolute -top-1 -right-3 text-lg text-emerald-300 font-sans">*</span>
                </span>
              </p>
              <div className="h-[2px] w-12 md:w-20 bg-gradient-to-l from-transparent to-emerald-400"></div>
            </div>
            
            <p className="text-white/80 text-base md:text-lg lg:text-xl mb-10 max-w-2xl mx-auto animate-slide-up leading-relaxed" style={{ animationDelay: '0.1s' }}>
              {content.homepage_hero_description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Button 
                onClick={openReservationModal}
                className="group w-full sm:w-auto text-base bg-emerald-500 hover:bg-emerald-400 text-white rounded-full px-8 py-4 font-semibold shadow-lg shadow-emerald-500/25 hover:shadow-emerald-400/40 transition-all hover:-translate-y-1" 
                data-testid="hero-cta-entretien"
              >
                {content.homepage_cta_primary}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Link to="/services">
                <Button variant="outline" className="w-full sm:w-auto bg-white/5 backdrop-blur-sm text-white border border-white/30 hover:bg-white/15 hover:border-white/50 rounded-full px-8 py-4 font-medium transition-all" data-testid="hero-cta-services">
                  Tous nos services
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="flex flex-col items-center gap-2 text-white/60">
            <span className="text-xs uppercase tracking-widest">Découvrir</span>
            <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-1.5">
              <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating CTA Banner - Reservation */}
      <section className="relative py-6 overflow-hidden" data-testid="reservation-banner">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500"></div>
        {/* Animated light streaks */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-yellow-300/20 rounded-full blur-2xl"></div>
          <div className="absolute -bottom-10 -right-10 w-60 h-60 bg-emerald-300/20 rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-white">
              <div className="hidden sm:flex w-14 h-14 bg-white/20 rounded-2xl items-center justify-center backdrop-blur-sm">
                <Calendar className="w-7 h-7" />
              </div>
              <div className="text-center lg:text-left">
                <p className="font-heading font-bold text-xl md:text-2xl">Réservez dès maintenant</p>
                <p className="text-emerald-100 text-sm">Choisissez le service adapté à vos besoins</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
              <Link to="/entretien">
                <Button className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-4 sm:px-5 py-2.5 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 text-sm" data-testid="banner-cta-entretien">
                  <Leaf className="w-4 h-4 mr-1.5" />
                  Entretien
                  <span className="hidden sm:inline ml-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">-50%</span>
                </Button>
              </Link>
              <Link to="/amenagement">
                <Button variant="outline" className="bg-transparent text-white border-white/40 hover:bg-white/10 rounded-full px-4 sm:px-5 py-2.5 font-medium text-sm" data-testid="banner-cta-amenagement">
                  Aménagement
                </Button>
              </Link>
              <Link to="/elagage">
                <Button variant="outline" className="bg-transparent text-white border-white/40 hover:bg-white/10 rounded-full px-4 sm:px-5 py-2.5 font-medium text-sm" data-testid="banner-cta-elagage">
                  Élagage
                </Button>
              </Link>
              <Link to="/conseil">
                <Button variant="outline" className="bg-transparent text-white border-white/40 hover:bg-white/10 rounded-full px-4 sm:px-5 py-2.5 font-medium text-sm" data-testid="banner-cta-conseil">
                  Conseil
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Wave separator */}
      <div className="relative h-16 bg-white overflow-hidden">
        <svg className="absolute bottom-0 w-full h-16" viewBox="0 0 1440 60" preserveAspectRatio="none">
          <path fill="#f5f5f4" d="M0,60 C360,20 720,40 1080,20 C1260,10 1380,30 1440,40 L1440,60 L0,60 Z"></path>
        </svg>
      </div>

      {/* Stats Section with light effects */}
      <section className="relative py-20 bg-stone-100 overflow-hidden" data-testid="stats-section">
        {/* Subtle light orbs */}
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-emerald-200/30 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-yellow-200/20 rounded-full blur-[80px]"></div>
        
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <p className="font-heading font-bold text-4xl md:text-5xl text-emerald-600 mb-2 group-hover:scale-110 transition-transform">
                  {stat.value}
                </p>
                <p className="text-stone-600 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curved separator */}
      <div className="relative h-20 bg-stone-100">
        <svg className="absolute bottom-0 w-full h-20" viewBox="0 0 1440 80" preserveAspectRatio="none">
          <path fill="#ffffff" d="M0,80 Q720,0 1440,80 L1440,80 L0,80 Z"></path>
        </svg>
      </div>

      {/* Features Section with parallax effect */}
      <section className="relative py-24 bg-white overflow-hidden" data-testid="features-section">
        {/* Background light effects */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-emerald-100/50 to-teal-100/30 rounded-full blur-[100px] -translate-x-1/2"></div>
        <div className="absolute bottom-20 left-0 w-80 h-80 bg-gradient-to-tr from-yellow-100/40 to-amber-100/20 rounded-full blur-[80px] translate-x-1/2"></div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-100 text-emerald-700 font-medium text-sm uppercase tracking-wide px-4 py-2 rounded-full mb-4">Pourquoi nous choisir</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-stone-900 mt-3">
              {content.homepage_features_title}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group bg-white rounded-3xl p-8 border border-stone-100 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-stone-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-stone-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diagonal separator into services */}
      <div className="relative h-24 bg-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-stone-50 to-teal-50/30 transform -skew-y-2 scale-110 origin-top-left"></div>
      </div>

      {/* Services Diagonal Scroll */}
      <section id="services" className="relative py-16 overflow-hidden" data-testid="services-section">
        {/* Background light effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-50/30 to-transparent"></div>
        <div className="absolute top-1/4 left-0 w-72 h-72 bg-emerald-200/20 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-yellow-200/15 rounded-full blur-[120px]"></div>
        
        <div className="container-custom mb-12 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="inline-block bg-emerald-100 text-emerald-700 font-medium text-sm uppercase tracking-wide px-4 py-2 rounded-full mb-4">Nos prestations</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-stone-900">
                {content.homepage_services_title}
              </h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:gap-3 transition-all bg-emerald-50 px-5 py-2.5 rounded-full hover:bg-emerald-100">
              Voir tous nos services
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
          </div>
        ) : services.length > 0 ? (
          <DiagonalScroll services={services} />
        ) : null}
      </section>

      {/* Organic blob separator */}
      <div className="relative h-32 overflow-hidden">
        <svg className="absolute w-full h-32" viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path fill="#f5f5f4" d="M0,64 C200,100 400,20 600,60 C800,100 1000,30 1200,70 C1350,95 1400,80 1440,64 L1440,120 L0,120 Z"></path>
        </svg>
      </div>

      {/* Credit d'impots Highlight */}
      <section className="relative py-20 bg-stone-100 overflow-hidden" data-testid="credit-highlight">
        {/* Light effects */}
        <div className="absolute top-0 left-1/3 w-80 h-80 bg-emerald-200/30 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-yellow-200/25 rounded-full blur-[80px]"></div>
        
        <div className="container-custom relative z-10">
          <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 rounded-3xl p-8 md:p-12 lg:p-16 shadow-2xl shadow-emerald-500/20 overflow-hidden relative">
            {/* Inner light effects */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-300/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-300/30 rounded-full blur-2xl"></div>
            
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 bg-white/20 text-white text-sm font-medium px-4 py-2 rounded-full mb-4">
                  <Euro className="w-4 h-4" />
                  Crédit d'impôts
                </div>
                <h3 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
                  Économisez 50% sur vos factures
                </h3>
                <p className="text-emerald-100 max-w-xl">
                  Bénéficiez de l'avance immédiate de crédit d'impôts. Vous ne payez que la moitié de vos prestations d'entretien de jardin !
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/credit-impot">
                  <Button className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                    En savoir plus
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wave separator */}
      <div className="relative h-20 bg-stone-100">
        <svg className="absolute bottom-0 w-full h-20" viewBox="0 0 1440 80" preserveAspectRatio="none">
          <path fill="#fafaf9" d="M0,40 Q360,80 720,40 Q1080,0 1440,40 L1440,80 L0,80 Z"></path>
        </svg>
      </div>

      {/* Reviews Section */}
      <section id="avis" className="relative py-24 bg-stone-50 overflow-hidden" data-testid="reviews-section">
        {/* Light effects */}
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-emerald-100/40 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 left-0 w-72 h-72 bg-yellow-100/30 rounded-full blur-[100px]"></div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block bg-amber-100 text-amber-700 font-medium text-sm uppercase tracking-wide px-4 py-2 rounded-full mb-4">Témoignages</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-stone-900">
              {content.homepage_reviews_title}
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <div className="inline-flex items-center gap-3 bg-white px-8 py-4 rounded-full shadow-lg">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-6 h-6 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-stone-700 font-semibold text-lg">{content.homepage_reviews_rating}</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600"></div>
        {/* Light effects */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute top-10 left-10 w-64 h-64 bg-yellow-300/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-emerald-300/30 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-teal-300/20 rounded-full blur-[100px]"></div>
        </div>
        
        <div className="container-custom text-center relative z-10">
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-6">
            {content.homepage_cta_title}
          </h2>
          <p className="text-emerald-100 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
            {content.homepage_cta_description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/entretien">
              <Button className="bg-white text-emerald-600 hover:bg-stone-100 rounded-full px-10 py-5 font-bold text-lg shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1" data-testid="cta-entretien">
                <Sparkles className="w-5 h-5 mr-2" />
                {content.homepage_cta_primary}
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="bg-transparent text-white border-2 border-white/50 hover:bg-white/10 rounded-full px-10 py-5 font-semibold text-lg" data-testid="cta-services">
                Voir tous nos services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="relative py-24 bg-white overflow-hidden" data-testid="blog-section">
        {/* Light effects */}
        <div className="absolute top-0 left-1/4 w-80 h-80 bg-emerald-100/30 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-0 right-1/3 w-64 h-64 bg-yellow-100/20 rounded-full blur-[80px]"></div>
        
        <div className="container-custom relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <span className="inline-block bg-blue-100 text-blue-700 font-medium text-sm uppercase tracking-wide px-4 py-2 rounded-full mb-4">Blog & Conseils</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-stone-900">
                {content.homepage_blog_title}
              </h2>
            </div>
            <Link to="/blog" className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:gap-3 transition-all">
              Voir tous les articles
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
